import { useWorkspaceText } from './interface-copy';
import { useEffect, useState } from 'react';
import { useCompany } from '@/company/context';
import { useAgentFeatures } from './agent-features';
import { useWorkspace } from './context';
import { EditOwnerContext } from './edit-owner';
import { WorkspaceIcon } from './icons';
import { EmptyState, ErrorState, LoadingState, PageHeading } from './primitives';
import {
  KnowledgePageView,
  PageEditor,
  ReviewList,
  TreeNav,
  useKnowledgeTree,
  usePendingProposals,
} from './knowledge-pages';
import type { PageFocus } from './knowledge-pages/page-view';

interface Selection {
  pageId: string | null;
  focus?: PageFocus;
  /** A new page being written under `parentId` (a page id, the root's included). */
  draft: { parentId: string } | null;
  /** On a phone the tree and the page take turns; this is the page's turn. */
  detail: boolean;
}

/** `#/workspace/knowledge?page=…&suggestion=…` names the page to open first. */
function linkedSelection(): Selection {
  const params = new URLSearchParams(location.hash.split('?')[1] ?? '');
  const pageId = params.get('page');
  const suggestion = params.get('suggestion') ?? undefined;
  return {
    pageId,
    focus: suggestion ? { suggestion } : undefined,
    draft: null,
    detail: Boolean(pageId),
  };
}

/** Keep the open page in the address without adding history entries or closing a canvas. */
function mirrorSelection(pageId: string | null, rootId: string | null) {
  if (!location.hash.startsWith('#/workspace/knowledge') || location.hash.includes('canvas='))
    return;
  const hash =
    pageId && pageId !== rootId
      ? `#/workspace/knowledge?page=${encodeURIComponent(pageId)}`
      : '#/workspace/knowledge';
  if (location.hash !== hash)
    history.replaceState(history.state, '', `${location.pathname}${location.search}${hash}`);
}

function Wiki() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const company = useCompany();
  const tree = useKnowledgeTree();
  const proposals = usePendingProposals();
  const [selection, setSelection] = useState<Selection>(linkedSelection);
  const tab = ws.knowledgeTab;
  const setTab = ws.setKnowledgeTab;
  const index = tree.index;
  const canPublish =
    tree.tree?.canPublish ?? company.data?.membership?.permissions?.publishCompany === true;
  const canSuggest = company.data?.membership?.permissions?.privateWork !== false;
  const selected =
    selection.pageId && index?.byId.has(selection.pageId)
      ? selection.pageId
      : (index?.root.id ?? null);
  useEffect(() => {
    if (index) mirrorSelection(selected, index.root.id);
  }, [selected, index]);
  const reviewCount =
    proposals.proposals?.length ??
    tree.tree?.pages.reduce((total, page) => total + page.openSuggestions, 0) ??
    0;
  // Unsaved text in the editor (a page or a new one) is never dropped
  // silently: these moves inside Knowledge ask with the workspace's one leave
  // dialog, as the sidebar, New chat, Recent chats and Back do.
  const open = (pageId: string, focus?: PageFocus) => {
    // Choosing the page already open changes nothing (an edit in progress stays).
    if (pageId === selected && !focus && !selection.draft) {
      setSelection((current) => ({ ...current, detail: true }));
      return;
    }
    ws.confirmLeave(() => setSelection({ pageId, focus, draft: null, detail: true }));
  };
  const tabButton = (value: 'pages' | 'review', label: string, count?: number) => (
    <button
      type="button"
      className={tab === value ? 'active' : ''}
      aria-pressed={tab === value}
      onClick={() => {
        if (value !== tab) ws.confirmLeave(() => setTab(value));
      }}
    >
      {label}
      {!!count && <span className="ws-review-count">{count}</span>}
    </button>
  );
  const parent = selection.draft ? index?.byId.get(selection.draft.parentId) : null;
  return (
    <>
      <div className="ws-view-tabs ws-knowledge-tabs">
        {tabButton('pages', 'Pages')}
        {tabButton('review', uiText('For review'), reviewCount)}
      </div>
      {tab === 'review' ? (
        <ReviewList
          proposals={proposals.proposals}
          error={proposals.error}
          onRetry={proposals.retry}
          index={index}
          canPublish={canPublish}
          onReview={(pageId, suggestion) => {
            setTab('pages');
            open(pageId, { suggestion });
          }}
        />
      ) : tree.error && !tree.tree ? (
        <ErrorState message={tree.error} onRetry={tree.retry} />
      ) : !index || !selected ? (
        <LoadingState label={uiText('Loading the knowledge pages…')} />
      ) : (
        <div className="ws-wiki" data-pane={selection.detail ? 'page' : 'tree'}>
          <TreeNav
            index={index}
            selectedId={selection.draft ? null : selected}
            onSelect={(id) => open(id)}
            onNewPage={
              canPublish || canSuggest
                ? () =>
                    ws.confirmLeave(() =>
                      setSelection({
                        pageId: selected,
                        draft: { parentId: selected },
                        detail: true,
                      }),
                    )
                : null
            }
          />
          <div className="ws-wiki-main">
            <button
              type="button"
              className="ws-text-button ws-wiki-back"
              onClick={() => {
                const back = () =>
                  setSelection((current) => ({ ...current, draft: null, detail: false }));
                // Going back drops a new page's draft; a page being edited only
                // hides, with its editor kept, so there is nothing to discard.
                if (selection.draft) ws.confirmLeave(back);
                else back();
              }}
            >
              {uiRich('{value1} All pages', {
                value1: <WorkspaceIcon name="chevron" size={14} className="ws-back-arrow" />,
              })}
            </button>
            {selection.draft && parent ? (
              <PageEditor
                key={`new\0${parent.id}`}
                target={{
                  kind: 'new',
                  parentId: parent.isRoot ? null : parent.id,
                  parentTitle: parent.title,
                  title: '',
                }}
                index={index}
                canPublish={canPublish}
                onCancel={() => setSelection((current) => ({ ...current, draft: null }))}
                onDone={(result) => {
                  if (result.outcome === 'created' && result.pageId) {
                    ws.notify(uiText('The page is created.'), null, 'success');
                    setSelection({ pageId: result.pageId, draft: null, detail: true });
                  } else {
                    ws.notify(
                      uiText(
                        'Your new page is suggested. A publisher decides whether it is added.',
                      ),
                      null,
                      'success',
                    );
                    setSelection({
                      pageId: parent.id,
                      focus: result.suggestionId ? { suggestion: result.suggestionId } : undefined,
                      draft: null,
                      detail: true,
                    });
                  }
                }}
              />
            ) : (
              <KnowledgePageView
                key={selected}
                pageId={selected}
                layout="full"
                focus={selection.pageId === selected ? selection.focus : undefined}
                tree={tree}
                navigation={{ openPage: open }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Company knowledge: the page tree with the open page (Pages) and every
 * suggestion waiting for a decision (For review).
 */
export function KnowledgePage() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const features = useAgentFeatures();
  const companyId = useCompany().data?.company?.id ?? '';
  // An older review link (`?review=` or `?proposal=`) opens For review.
  useEffect(() => {
    const params = new URLSearchParams(location.hash.split('?')[1] ?? '');
    if (params.get('review') || params.get('proposal')) ws.setKnowledgeTab('review');
  }, []);
  return (
    <div className="ws-knowledge-page">
      <PageHeading
        eyebrow={uiText('{company} workspace', { company: ws.companyName })}
        title={uiText('Company knowledge')}
        description={uiText(
          'What Poneva knows about your company, page by page. Every change is kept.',
        )}
      />
      {!features ? (
        <LoadingState label={uiText('Loading company knowledge…')} />
      ) : !features.knowledgePages ? (
        <EmptyState
          icon="knowledge"
          title={uiText('Knowledge pages are not enabled in this workspace.')}
          description={uiText('Ask your Poneva contact to switch them on for your company.')}
        />
      ) : (
        // A company switch starts clean: no page, draft or focus carried over.
        // Its editor belongs to this view, not to a canvas docked beside it.
        <EditOwnerContext.Provider value="view">
          <Wiki key={companyId} />
        </EditOwnerContext.Provider>
      )}
    </div>
  );
}
